<template>
  <div class="cws-container blog-wrapper">
    <b-overlay
        :show="isLoadingPlaylist"
        rounded="sm"
    >
      <b-row>
        <b-col cols="12">
          <b-card
              title="SIQ Импорт"
              class="mb-3"
              border-variant="primary"
          >
            <b-card-text>
              Загрузка SIQ-файла для импорта песен
            </b-card-text>

            <b-form-group
                label="SIQ-Файл"
                label-for="siq-file"
            >
              <b-button
                  variant="outline-primary"
                  @click="$refs.refInputUploadFile.click()"
              >
                <input
                    id="siq-file"
                    ref="refInputUploadFile"
                    type="file"
                    class="d-none"
                    @input="inputSiqFileRenderer"
                />
                <span class="d-none d-sm-inline">Upload</span>
                <feather-icon
                    icon="EditIcon"
                    class="d-inline d-sm-none"
                />
              </b-button>
            </b-form-group>
          </b-card>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-card v-if="playlistDetail" no-body>
            <b-card-header class="pb-50">
              <h5 class="d-flex align-items-center mb-0">
                {{ playlistDetail.title || 'Список треков' }}
                <b-badge variant="light-primary" class="ml-1">{{ playlistDetail.tracks.length }}</b-badge>
              </h5>
            </b-card-header>
            <b-card-body>
              <!-- Описание приходит из SIQ-файла — выводим как текст, не как HTML -->
              <div
                  v-if="playlistDetail.description"
                  class="blog-content mb-1"
                  v-text="playlistDetail.description"
              />
              <playlist-tracks-table
                  :tracks="playlistDetail.tracks"
                  :is-loading="isLoadingPlaylist"
                  @add-track="addTrack"
              />
            </b-card-body>
          </b-card>

          <b-card
              v-else
              title="Данных нет"
          >
            <b-card-text>
              Загрузите SIQ-файл
            </b-card-text>
          </b-card>
        </b-col>
      </b-row>
    </b-overlay>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCardText,
  BCol,
  BFormGroup,
  BOverlay,
  BRow,
} from 'bootstrap-vue'
import Vue from 'vue'
import { onUnmounted, ref } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { isSubmissionResponse, notifySubmitted } from '@/libs/moderation'
import store from '@/store'
import musicStoreModule from '@/views/db-music/musicStoreModule'
import PlaylistTracksTable from '@/views/db-music/PlaylistTracksTable.vue'
import { useInputImageRenderer } from '@core/comp-functions/forms/form-utils'

export default {
  components: {
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCardText,
    BCol,
    BFormGroup,
    BOverlay,
    BRow,
    PlaylistTracksTable,
  },
  setup() {
    const STORE_MODULE_NAME = 'playlist'
    const toast = useToast()

    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const playlistDetail = ref(null)
    const isLoadingPlaylist = ref(false)
    const playlistCacheId = ref(null)
    const refInputUploadFile = ref(null)

    const getErrorText = error => error.response?.data?.message
      || error.response?.data?.error
      || error.message
      || 'Произошла ошибка'

    const fetchPlaylistData = async () => {
      if (!playlistCacheId.value) return

      const res = await store.dispatch('playlist/sigameImportList', { file: null, id: playlistCacheId.value })
      playlistDetail.value = res.data
    }

    const { inputImageRenderer: inputSiqFileRenderer } = useInputImageRenderer(refInputUploadFile, async () => {
      const file = refInputUploadFile.value.files[0]
      isLoadingPlaylist.value = true

      try {
        const res = await store.dispatch('playlist/sigameImportList', { file, id: null })
        playlistDetail.value = res.data
        playlistCacheId.value = res.data.cache_id
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Не удалось загрузить SIQ-файл',
            text: getErrorText(error),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingPlaylist.value = false
      }
    })

    const addTrack = async track => {
      const firstArtist = track.artists && track.artists[0]

      isLoadingPlaylist.value = true

      try {
        let artistId = firstArtist && firstArtist.id ? firstArtist.id : null
        let isArtistCreated = false

        if (!artistId) {
          const artistResponse = await store.dispatch('playlist/createArtist', {
            title: firstArtist ? firstArtist.name : null,
          })
          artistId = artistResponse.data.data.id
          isArtistCreated = true
        }

        const addResponse = await store.dispatch('playlist/addTrack', {
          id: artistId,
          queryParams: { title: track.title },
        })

        // Автор: трек ушёл на одобрение — строку не обновляем, список не перезагружаем
        if (isSubmissionResponse(addResponse)) {
          notifySubmitted(toast)
          return
        }

        const newTrackId = addResponse.data.track_id
          || (addResponse.data.data ? addResponse.data.data.id : addResponse.data.id)

        if (newTrackId) {
          // Точечно обновляем строку: повторный разбор всего пакета не нужен
          if (isArtistCreated && firstArtist) {
            Vue.set(firstArtist, 'id', artistId)
          }
          Vue.set(track, 'id', newTrackId)
        } else {
          // Сервер не вернул идентификатор трека — остаётся перезагрузить список целиком
          await fetchPlaylistData()
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Не удалось добавить трек',
            text: getErrorText(error),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingPlaylist.value = false
      }
    }

    return {
      playlistDetail,
      isLoadingPlaylist,
      addTrack,
      inputSiqFileRenderer,
      refInputUploadFile,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-blog.scss';
</style>
